import { motion } from "framer-motion";
import { PlayCircle } from "lucide-react";
import { PhoneMockups } from "@/components/PhoneMockups";
import { StoreBadges } from "@/components/StoreBadges";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export const Hero = () => {
  const scrollToWorks = () =>
    document.getElementById("workflow")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative overflow-hidden">
      {/* Background accents */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-32 h-[500px] w-[500px] rounded-full bg-[#00DC82]/[0.07] blur-[120px]" />
        <div className="absolute top-20 right-0 h-[400px] w-[400px] rounded-full bg-[#0F1629] blur-[100px]" />
        {/* faint dot pattern — only visible on close inspection */}
        <div
          className="absolute inset-0 opacity-[0.5]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.035) 1px, transparent 0)",
            backgroundSize: "38px 38px",
            maskImage:
              "radial-gradient(ellipse 90% 70% at 50% 30%, black 40%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 90% 70% at 50% 30%, black 40%, transparent 100%)",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 pt-36 lg:pt-44 pb-20 flex flex-col lg:flex-row items-center gap-16">
        {/* Left — content */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="flex-1 flex flex-col gap-7 w-full max-w-2xl z-10"
        >
          <motion.div variants={item}>
            <span
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 rounded-full border border-[#00DC82]/20 bg-[#00DC82]/[0.06] px-4 py-1.5 text-xs font-medium tracking-wide text-[#00DC82]"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#00DC82] animate-pulse" />
              Available now on Google Play
            </span>
          </motion.div>

          <motion.h1
            variants={item}
            data-testid="hero-headline"
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]"
          >
            Professional Student Progress Management
            <br />
            <span className="text-[#00DC82]">for Driving Instructors</span>
          </motion.h1>

          <motion.p
            variants={item}
            data-testid="hero-subheading"
            className="text-base md:text-lg text-slate-400 leading-relaxed max-w-xl"
          >
            Track student progress, build complete learning records and create professional
            branded reports — all in one place.
          </motion.p>

          {/* CTA */}
          <motion.div
            variants={item}
            className="flex flex-col items-start gap-5 mt-2 scroll-mt-28"
          >
            <StoreBadges />

            <button
              type="button"
              onClick={scrollToWorks}
              data-testid="see-how-it-works-button"
              className="group flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              <PlayCircle
                size={18}
                className="text-[#00DC82] group-hover:scale-110 transition-transform duration-200"
              />
              See How It Works
            </button>
          </motion.div>

          {/* Coverage / supported assessment systems */}
          <motion.p
            variants={item}
            data-testid="coverage-line"
            className="text-xs text-slate-500 tracking-wide"
          >
            🇮🇪 Ireland (RSA) &nbsp;•&nbsp; 🇬🇧 Great Britain (DVSA)
          </motion.p>
        </motion.div>

        {/* Right — phone mockups */}
        <div className="flex-1 w-full flex items-center justify-center">
          <PhoneMockups />
        </div>
      </div>
    </section>
  );
};
